import { Container, Sprite, Texture } from 'pixi.js'

/**
 * A minimal additive particle field for board effects.
 *
 * Deliberately dependency-free: a particle emitter library would be another
 * package inside the operator's iframe, and everything we need here is a
 * pool of sprites with `blendMode = 'add'`. Additive blending is what makes
 * the burst read as light rather than as confetti — overlapping sprites sum
 * up towards white instead of covering each other.
 *
 * Sprites share one procedurally built radial-gradient texture, tinted per
 * particle. One texture means one draw batch, so the whole field costs about
 * as much as a single sprite to render.
 *
 * The field owns no ticker: the caller drives `update(dtMs)` from its own
 * loop, so the effects stop exactly when the board's animations do.
 *
 * `docs/vfx-guide.md` carries the rules this file is built around — why
 * shape beats brightness over painted art, why a long axis reads as a
 * blade, which fade curve fits what, and how to bench a change. Read it
 * before adding a shape or retuning one.
 */

interface Particle {
  sprite: Sprite
  /** Velocity in px/s. */
  vx: number
  vy: number
  /** Seconds lived and total lifetime — progress is age/life. */
  age: number
  life: number
  size: number
  /** Extra px/s² pulling the particle down. Embers use a negative value. */
  gravity: number
  /** Alpha at birth; the fade curve scales this, never exceeds it. */
  alpha0: number
  spin: number
  /** Height multiplier: >1 turns the round glow into a falling streak. */
  stretch: number
  /** Horizontally mirrored. Re-applied every frame: width sets reset it. */
  flip: boolean
  /** Fade curve. Water keeps its body and dies late; sparks fade fast. */
  fade: FadeCurve
  /** Size change over the lifetime: −0.5 halves it, +1 doubles it. */
  grow: number
  /**
   * Air resistance, per second. Sparks need it — they shoot out and coast,
   * which is most of what makes a burst read as an explosion. Water does
   * not: over half a second of falling it is negligible, and leaving it on
   * would eat the sideways drift that gives a jet its arc.
   */
  drag: number
  /** Polar motion about an anchor. Null for the ordinary straight flight. */
  orbit: OrbitState | null
}

/**
 * A particle circling a point instead of flying straight.
 *
 * Needed because a vortex cannot be faked with linear velocity. Launching
 * sprites along the tangent of a circle gives them the right heading for
 * one instant and then they leave on that heading — the ring blows apart
 * into a puff. Nothing about a straight line ever curls, and curl is the
 * only thing a whirlwind is.
 *
 * So these integrate in polar coordinates: the angle advances at `omega`
 * and the radius at `vr`, which is exactly a spiral. Drag, gravity and
 * `vx/vy` do not apply — a particle is either in the orbit or in the air.
 */
interface OrbitState {
  cx: number
  cy: number
  /** Current polar position about the anchor. */
  angle: number
  radius: number
  /** Angular speed, rad/s. Signed: the sign is which way the vortex turns. */
  omega: number
  /** Radial speed, px/s. Positive drifts outward. */
  vr: number
  /** Angular drag, per second — the swirl winds down instead of spinning on. */
  spinDown: number
  /** Keep the sprite's long axis on the tangent as it travels. */
  faceTangent: boolean
}

/**
 * `square` — bright at birth, gone well before the end of the lifetime:
 * the right read for a spark. `late` holds full opacity and only fades in
 * the last third, which is how a water droplet behaves. `fog` fades in as
 * well as out — a haze blob that appears at full opacity pops, and the eye
 * catches the pop long before it notices the fog.
 *
 * `fall` is `late` with a fade-in long enough to survive gravity. Under
 * acceleration the first fifth of a droplet's LIFE is only about the first
 * twentieth of its DISTANCE, so `late`'s ramp is spent inside a few pixels
 * of the lip and the droplets read as popping into existence on the edge.
 * Ramping over the first 60 % of the life spreads that over roughly the
 * top third of the drop, which is where a painted fall is a solid sheet
 * anyway — the particles emerge out of it instead of starting on top of it.
 */
export type FadeCurve = 'square' | 'late' | 'fog' | 'fall'

export interface BurstOptions {
  /** Tint of the particles, usually the symbol's own colour. */
  color: number
  /** How many sparks fly out. */
  count?: number
  /** Base outward speed, px/s. */
  speed?: number
  /** Base particle diameter, px. */
  size?: number
  /** Base lifetime, seconds. */
  life?: number
  gravity?: number
}

/** One particle's full state at birth, for the generic `emit`. */
export interface EmitSpec {
  vx: number
  vy: number
  life: number
  size: number
  gravity: number
  alpha0: number
  color: number
  spin: number
  stretch?: number
  fade?: FadeCurve
  grow?: number
  drag?: number
  /** Index into the field's texture fleet (clouds). Default 0. */
  tex?: number
  /** Mirror horizontally — doubles the apparent variety of a small fleet. */
  flip?: boolean
  /**
   * Birth rotation, radians. Only directional shapes need it: a swirl has
   * to point along its own motion, and the default (random for round
   * sprites, zero for stretched ones) cannot know which way that is.
   */
  rotation?: number
  /**
   * Circle this anchor instead of flying straight. The spawn position sets
   * the starting angle and radius; `vx`/`vy`, `gravity` and `drag` are
   * ignored for the duration.
   */
  orbit?: {
    cx: number
    cy: number
    omega: number
    vr: number
    /** Angular drag per second. 0 keeps the vortex turning at one speed. */
    spinDown?: number
    faceTangent?: boolean
  }
}

/** Soft round glow, built once and shared by every particle. */
function glowTexture(): Texture {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return Texture.WHITE
  const r = size / 2
  const g = ctx.createRadialGradient(r, r, 0, r, r, r)
  // A hot white core that falls off fast: a linear ramp looks like a flat
  // disc once several particles overlap, this one keeps a visible centre.
  g.addColorStop(0, 'rgba(255,255,255,1)')
  g.addColorStop(0.25, 'rgba(255,255,255,0.72)')
  g.addColorStop(0.55, 'rgba(255,255,255,0.22)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  return Texture.from(canvas)
}

/** Deterministic RNG: every session bakes identical cloud textures. */
function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Tileable value noise + billow fBm — the fluff of the cloud edges. */
function makeNoise(rng: () => number, size: number): (x: number, y: number) => number {
  const g = new Float32Array(size * size)
  for (let i = 0; i < g.length; i++) g[i] = rng()
  const smooth = (t: number): number => t * t * (3 - 2 * t)
  return (x, y) => {
    // True modulo: shading samples reach outside the canvas, and a
    // negative index would read undefined → NaN → black pixels.
    const xi = ((Math.floor(x) % size) + size) % size
    const yi = ((Math.floor(y) % size) + size) % size
    const xf = x - Math.floor(x)
    const yf = y - Math.floor(y)
    const x1 = (xi + 1) % size
    const y1 = (yi + 1) % size
    const a = g[yi * size + xi]
    const b = g[yi * size + x1]
    const c = g[y1 * size + xi]
    const d = g[y1 * size + x1]
    const u = smooth(xf)
    const v = smooth(yf)
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v
  }
}

function billowFbm(rng: () => number, octaves: number): (x: number, y: number) => number {
  const noise = makeNoise(rng, 64)
  return (x, y) => {
    let sum = 0
    let amp = 1
    let freq = 1
    let norm = 0
    for (let o = 0; o < octaves; o++) {
      // |2n−1| folds smooth hills into cauliflower ridges.
      sum += (1 - Math.abs(2 * noise(x * freq, y * freq) - 1)) * amp
      norm += amp
      amp *= 0.55
      freq *= 2.1
    }
    return sum / norm
  }
}

/** Shape knobs of one baked cumulus. Tuned on `tmp-cloud-preview.html`. */
interface CumulusSpec {
  seed: number
  w: number
  h: number
  /** How many extra towers may join the central heap. */
  towers: number
  /** Tower height in canvas fractions — lower = flatter cloud. */
  towerH: number
  /** Base sphere radius in canvas-width fractions. */
  puffR: number
  /** Noise frequency of the edge fluff. */
  detail: number
  /** Amplitude of the edge fray. */
  fray: number
  /** Density threshold where the visible surface sits. */
  cut: number
  /** Width of the translucency ramp at that surface. */
  soft: number
  /** Weight of the relief accent in the shading. */
  shade: number
  /** Overall opacity baked into the texture. */
  alpha: number
}

/**
 * A cumulus heap baked into a texture: metaball towers for the rounded
 * mass, billow fBm pushing wisps out past the surface, smooth top-lit
 * shading with blue-grey pockets. The recipe was chosen interactively on
 * `tmp-cloud-preview.html` ("V2 м'якша, світліша") — change it there
 * first, where the variants render side by side over the real sky.
 *
 * Pure noise reads as rock and pure discs read as dots; it takes both.
 */
function cumulusTexture(o: CumulusSpec): Texture {
  const { w, h } = o
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return Texture.WHITE
  const rng = mulberry32(o.seed)
  const crinkle = billowFbm(mulberry32(o.seed + 99), 5)

  // A HEAP, not a strip: towers crowd around the centre, the central one
  // is the tallest, and the belly is lumpy — spheres on one level fuse
  // into a smooth floor that shading then cuts with a hard "snow line".
  const balls: Array<[number, number, number]> = []
  const towerN = 2 + Math.floor(rng() * o.towers)
  for (let t = 0; t < towerN; t++) {
    const off = towerN === 1 ? 0 : (t - (towerN - 1) / 2) / (towerN - 1)
    const cx = 0.5 + off * 0.34 + (rng() - 0.5) * 0.05
    const dome = Math.cos(off * Math.PI * 0.9)
    const height = o.towerH * (0.4 + dome * 0.35) * (0.85 + rng() * 0.3)
    const K = 7 + Math.floor(rng() * 4)
    for (let k = 0; k < K; k++) {
      const f = k / (K - 1)
      const r = o.puffR * (1 - f * 0.25) * (0.75 + rng() * 0.5)
      const spreadX = o.puffR * (1 - f * 0.72) * 0.6
      balls.push([cx + (rng() - 0.5) * 2 * spreadX, 0.74 - height * f - rng() * 0.02, r])
    }
  }
  for (let i = 0; i < 5; i++)
    balls.push([
      0.28 + 0.44 * (i + 0.5) / 5 + (rng() - 0.5) * 0.05,
      0.75 + (rng() - 0.5) * 0.11,
      o.puffR * (0.9 + rng() * 0.6),
    ])

  const smooth = (t: number): number => {
    t = Math.min(1, Math.max(0, t))
    return t * t * (3 - 2 * t)
  }
  // The metaball field reaches 1.5× past each sphere: the visible surface
  // sits at `cut`, and the halo of weak field OUTSIDE it is where the
  // noise pushes the frayed, smoky wisps. A field that ends at the
  // surface can only make a hard silhouette.
  const aspect = w / h
  const density = (X: number, Y: number): number => {
    const u = X / w
    const v = Y / h
    let b = 0
    for (const [bx, by, r0] of balls) {
      const r = r0 * 1.5
      const dx = ((u - bx) * aspect) / r
      const dy = (v - by) / r
      const q = 1 - (dx * dx + dy * dy)
      if (q > 0) b += q * q * 0.55
    }
    const band = smooth(b / 0.1) * (1 - smooth((b - 0.85) / 0.6))
    const d = b + (crinkle(u * o.detail, v * o.detail) - 0.42) * o.fray * band
    // The underside dissolves instead of ending in a cut.
    return d * (1 - smooth((v - 0.84) / 0.16))
  }

  // Density is sampled once into a grid; the shading pass reads
  // neighbours from it instead of re-evaluating every metaball 3×.
  const dens = new Float32Array(w * h)
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) dens[y * w + x] = density(x, y)

  const img = ctx.createImageData(w, h)
  const px = img.data
  const eps = 6
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const d = dens[y * w + x]
      // Wide translucency ramp: full opacity only near the core,
      // everything toward the edge is gauze.
      const alpha = smooth((d - o.cut) / o.soft) * (0.55 + 0.45 * smooth((d - o.cut) / 0.9))
      if (alpha <= 0.004) continue
      // Smooth vertical light (sunlit crown → shaded floor) carries the
      // volume; the surface gradient only accents local billows, and its
      // contribution is CAPPED — near the surface the gradient is huge
      // and any plain weight slams the shading into a solid white cap.
      const below = dens[Math.min(h - 1, y + eps) * w + x]
      const above = dens[Math.max(0, y - eps) * w + x]
      const relief = Math.min(0.14, Math.max(-0.3, (below - above) * o.shade * 0.35))
      const mottle = (crinkle((x / w) * 3.4 + 31, (y / h) * 3.4) - 0.5) * 3 * 0.16
      let lit = 1 - (y / h) * 0.55 + relief + mottle
      lit = Math.min(1, Math.max(0.5, lit))
      const i = (y * w + x) * 4
      px[i] = 120 + (255 - 120) * lit
      px[i + 1] = 138 + (255 - 138) * lit
      px[i + 2] = 160 + (255 - 160) * lit
      px[i + 3] = 255 * Math.min(1, alpha) * o.alpha
    }
  }
  ctx.putImageData(img, 0, 0)
  return Texture.from(canvas)
}

/**
 * The fleet: two full heaps and two flatter, hazier banks. Variety comes
 * from here, not from distorting one texture — a squashed cumulus keeps
 * its round billows, which reads as a photo scaled wrong.
 */
const CUMULUS_VARIANTS: CumulusSpec[] = [
  { seed: 8, w: 288, h: 216, towers: 2, towerH: 0.55, puffR: 0.2, detail: 6, fray: 0.35, cut: 0.17, soft: 0.42, shade: 2.2, alpha: 0.8 },
  { seed: 17, w: 288, h: 216, towers: 3, towerH: 0.6, puffR: 0.185, detail: 7, fray: 0.42, cut: 0.17, soft: 0.4, shade: 2.4, alpha: 0.78 },
  { seed: 5, w: 288, h: 150, towers: 2, towerH: 0.34, puffR: 0.17, detail: 6, fray: 0.38, cut: 0.16, soft: 0.44, shade: 2.0, alpha: 0.72 },
  { seed: 29, w: 288, h: 132, towers: 2, towerH: 0.28, puffR: 0.15, detail: 7, fray: 0.45, cut: 0.15, soft: 0.5, shade: 1.8, alpha: 0.62 },
]

/**
 * A four-point flare: two tapered spikes crossed over a small hot core.
 *
 * The glow blob cannot carry a gem's sparkle over bright art. Additive
 * light works by raising brightness, and the painted gold and sky are
 * already near the top of the range — the aura is physically there and
 * perceptually invisible. A star is legible for a different reason: the
 * backdrop contains no shape like it, so the eye reads "something is
 * happening" from the silhouette rather than from the level, and it does
 * that at an opacity low enough to leave the painted facets untouched.
 */
function starTexture(): Texture {
  const size = 128
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return Texture.WHITE
  const c = size / 2

  // Spikes as filled kites rather than strokes: a stroked line has a
  // constant width and reads as a cross-hair, while a spike that narrows
  // to a point reads as light.
  const spike = (len: number, halfWidth: number, rotation: number): void => {
    ctx.save()
    ctx.translate(c, c)
    ctx.rotate(rotation)
    const g = ctx.createLinearGradient(0, -len, 0, len)
    g.addColorStop(0, 'rgba(255,255,255,0)')
    g.addColorStop(0.5, 'rgba(255,255,255,0.95)')
    g.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(0, -len)
    ctx.lineTo(halfWidth, 0)
    ctx.lineTo(0, len)
    ctx.lineTo(-halfWidth, 0)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
  }
  // The long axis is vertical: these sit on cut stones, and a taller flare
  // follows the rhombus it comes out of. The diagonals are short and thin,
  // which is what keeps it from looking like a snowflake.
  spike(c * 0.98, c * 0.05, 0)
  spike(c * 0.72, c * 0.045, Math.PI / 2)
  spike(c * 0.34, c * 0.03, Math.PI / 4)
  spike(c * 0.34, c * 0.03, -Math.PI / 4)

  // A small hot centre where the spikes meet — without it the crossing
  // point is the dimmest part of the star, which looks like a mistake.
  const core = ctx.createRadialGradient(c, c, 0, c, c, c * 0.3)
  core.addColorStop(0, 'rgba(255,255,255,1)')
  core.addColorStop(0.4, 'rgba(255,255,255,0.5)')
  core.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = core
  ctx.fillRect(0, 0, size, size)
  return Texture.from(canvas)
}

/** Which shape a field's sprites use. */
export type ParticleShape = 'glow' | 'cloud' | 'star'

let sharedGlow: Texture | null = null
let sharedStar: Texture | null = null
let sharedClouds: Texture[] | null = null

/** How the field's sprites combine with what is behind them. */
export type ParticleBlend = 'add' | 'normal'

export class ParticleField {
  /** The layer the caller places in its own stage order. */
  readonly view = new Container()
  private live: Particle[] = []
  private pool: Sprite[] = []
  /** One texture for glow fields, the whole fleet for cloud fields. */
  private textures: Texture[]
  private blend: ParticleBlend

  /**
   * `add` is right for light — fire, sparks, magic. Water and mist are not
   * light sources: over a bright sky additive white blows out to a flat
   * white blob, so those fields ask for `normal` and carry their softness
   * in the alpha instead.
   */
  constructor(blend: ParticleBlend = 'add', shape: ParticleShape = 'glow', textures?: Texture[]) {
    if (textures && textures.length > 0) {
      // The caller brings its own art — theme-shipped cloud PNGs. The
      // procedural fleet below is the fallback for themes that ship none.
      this.textures = textures
    } else if (shape === 'cloud') {
      sharedClouds ??= CUMULUS_VARIANTS.map(cumulusTexture)
      this.textures = sharedClouds
    } else if (shape === 'star') {
      sharedStar ??= starTexture()
      this.textures = [sharedStar]
    } else {
      sharedGlow ??= glowTexture()
      this.textures = [sharedGlow]
    }
    this.blend = blend
    // Particles never take a click meant for the board underneath.
    this.view.eventMode = 'none'
  }

  /**
   * A ring of sparks plus a short bright flash at the centre — the shape of
   * a symbol popping. Coordinates are in the parent container's space.
   */
  burst(x: number, y: number, opts: BurstOptions): void {
    const count = opts.count ?? 14
    const speed = opts.speed ?? 210
    const size = opts.size ?? 22
    const life = opts.life ?? 0.55
    const gravity = opts.gravity ?? 340

    // The flash: one big, short-lived, fast-shrinking particle. It hides the
    // moment the symbol disappears, which is otherwise the weakest frame.
    this.emit(x, y, {
      vx: 0,
      vy: 0,
      life: life * 0.45,
      size: size * 3.4,
      gravity: 0,
      alpha0: 0.85,
      color: opts.color,
      spin: 0,
    })

    // The sparks: evenly spread around the circle with jitter, so the ring
    // reads as an explosion and not as a mechanical star.
    const step = (Math.PI * 2) / count
    for (let i = 0; i < count; i++) {
      const angle = i * step + (Math.random() - 0.5) * step
      const v = speed * (0.55 + Math.random() * 0.9)
      this.emit(x, y, {
        vx: Math.cos(angle) * v,
        vy: Math.sin(angle) * v,
        life: life * (0.7 + Math.random() * 0.7),
        size: size * (0.5 + Math.random() * 0.8),
        gravity,
        alpha0: 0.75 + Math.random() * 0.25,
        color: opts.color,
        spin: (Math.random() - 0.5) * 6,
      })
    }
  }

  /**
   * Slow embers drifting upward — the "magic" idle, for scatters and bombs.
   * Cheap enough to run on several cells at once.
   */
  embers(x: number, y: number, opts: BurstOptions): void {
    const count = opts.count ?? 8
    for (let i = 0; i < count; i++) {
      this.emit(x + (Math.random() - 0.5) * 40, y + (Math.random() - 0.5) * 40, {
        vx: (Math.random() - 0.5) * 26,
        vy: -18 - Math.random() * 34,
        life: (opts.life ?? 1.1) * (0.7 + Math.random() * 0.6),
        size: (opts.size ?? 14) * (0.5 + Math.random() * 0.7),
        // Negative gravity: embers accelerate upward like hot air.
        gravity: opts.gravity ?? -30,
        alpha0: 0.5 + Math.random() * 0.4,
        color: opts.color,
        spin: 0,
      })
    }
  }

  /**
   * Spawn one particle. Public so that a caller with its own motion idea —
   * a falling water jet, drifting fog — can compose it without every shape
   * having to become a method here.
   */
  emit(x: number, y: number, p: EmitSpec): void {
    const sprite = this.pool.pop() ?? this.makeSprite()
    const texture = this.textures[Math.min(p.tex ?? 0, this.textures.length - 1)]
    // `size` is the WIDTH; the height follows the texture's own aspect, so
    // a flat cloud bank stays flat. `stretch` multiplies on top of that —
    // for square textures it behaves exactly as before.
    const stretch = (p.stretch ?? 1) * (texture.height / texture.width)
    sprite.texture = texture
    sprite.visible = true
    sprite.x = x
    sprite.y = y
    // A non-round particle is directional — spinning it would point the
    // streak (or tip the cloud) sideways, so only round ones roll.
    sprite.rotation = p.rotation ?? (stretch === 1 ? Math.random() * Math.PI : 0)
    sprite.tint = p.color
    sprite.alpha = p.alpha0
    sprite.width = p.size
    sprite.height = p.size * stretch
    // A pooled sprite may arrive mirrored from its previous life; set the
    // sign explicitly rather than toggling it.
    sprite.scale.x = Math.abs(sprite.scale.x) * (p.flip ? -1 : 1)
    this.view.addChild(sprite)
    this.live.push({
      sprite,
      vx: p.vx,
      vy: p.vy,
      age: 0,
      life: p.life,
      size: p.size,
      gravity: p.gravity,
      alpha0: p.alpha0,
      spin: p.spin,
      stretch,
      flip: p.flip ?? false,
      fade: p.fade ?? 'square',
      grow: p.grow ?? -0.55,
      drag: p.drag ?? 1.6,
      // The polar state is derived from where the caller asked for the
      // particle, not passed in: a caller that has already worked out the
      // spawn point should not have to restate it as an angle too.
      orbit: p.orbit
        ? {
            cx: p.orbit.cx,
            cy: p.orbit.cy,
            angle: Math.atan2(y - p.orbit.cy, x - p.orbit.cx),
            radius: Math.hypot(x - p.orbit.cx, y - p.orbit.cy),
            omega: p.orbit.omega,
            vr: p.orbit.vr,
            spinDown: p.orbit.spinDown ?? 0,
            faceTangent: p.orbit.faceTangent ?? false,
          }
        : null,
    })
  }

  /** How many texture variants this field can draw. */
  get textureCount(): number {
    return this.textures.length
  }

  private makeSprite(): Sprite {
    const sprite = new Sprite(this.textures[0])
    sprite.anchor.set(0.5)
    sprite.blendMode = this.blend
    return sprite
  }

  /** Advance every live particle. Safe to call with no particles alive. */
  update(dtMs: number): void {
    if (this.live.length === 0) return
    const dt = Math.min(dtMs, 50) / 1000
    const survivors: Particle[] = []
    for (const p of this.live) {
      p.age += dt
      if (p.age >= p.life) {
        p.sprite.visible = false
        this.view.removeChild(p.sprite)
        this.pool.push(p.sprite)
        continue
      }
      const t = p.age / p.life
      if (p.orbit) {
        const o = p.orbit
        if (o.spinDown > 0) o.omega *= 1 - Math.min(1, o.spinDown * dt)
        o.angle += o.omega * dt
        o.radius += o.vr * dt
        p.sprite.x = o.cx + Math.cos(o.angle) * o.radius
        p.sprite.y = o.cy + Math.sin(o.angle) * o.radius
        // A ribbon that keeps its birth rotation while its position sweeps
        // round the centre skids sideways like a dropped card. Riding the
        // tangent is what makes it look attached to the current.
        p.sprite.rotation = o.faceTangent
          ? o.angle + Math.PI / 2 + p.spin * p.age
          : p.sprite.rotation + p.spin * dt
      } else {
        p.vy += p.gravity * dt
        if (p.drag > 0) {
          const drag = 1 - Math.min(1, p.drag * dt)
          p.vx *= drag
          p.vy *= drag
        }
        p.sprite.x += p.vx * dt
        p.sprite.y += p.vy * dt
        p.sprite.rotation += p.spin * dt
      }
      // A linear fade leaves the tail visible for too long and the burst
      // looks like it stalls; `late` exists for water, which must stay
      // solid on its way down and only dissolve at the bottom.
      // `late` fades IN over the first fifth and dissolves over the last
      // half. Both ends matter: droplets popping in at full opacity draw a
      // hard edge across the lip of the fall, and droplets staying solid to
      // the end read as hanging past the water.
      p.sprite.alpha =
        p.fade === 'late'
          ? p.alpha0 * Math.min(1, t * 5, (1 - t) * 2)
          : p.fade === 'fall'
            ? p.alpha0 * Math.min(1, t * 1.7, (1 - t) * 2.2)
            : p.fade === 'fog'
              ? p.alpha0 * Math.sin(t * Math.PI)
              : p.alpha0 * (1 - t) * (1 - t)
      // Sparks shrink as they cool, fog expands as it disperses, water does
      // neither — a shrinking droplet reads as flying away from the camera.
      const scale = 1 + p.grow * t
      p.sprite.width = p.size * scale
      p.sprite.height = p.size * scale * p.stretch
      // After the width set, not before: whether the setter preserves the
      // scale's sign is a Pixi implementation detail this must not lean on.
      if (p.flip && p.sprite.scale.x > 0) p.sprite.scale.x *= -1
      survivors.push(p)
    }
    this.live = survivors
  }

  /** Drop everything on screen without waiting for the particles to die. */
  clear(): void {
    for (const p of this.live) {
      p.sprite.visible = false
      this.view.removeChild(p.sprite)
      this.pool.push(p.sprite)
    }
    this.live = []
  }

  destroy(): void {
    this.clear()
    for (const s of this.pool) s.destroy()
    this.pool = []
    this.view.destroy({ children: true })
  }
}
